/**
 * Build a flat option list for SearchableSelect from the metadata catalog,
 * grouped by category prefix so the user can filter via typeahead.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowOptimizerMetricMeta } from '@trafficpulse/shared';

const CATEGORY_ORDER = ['traffic', 'conversions', 'financial', 'rates', 'advanced', 'cap'] as const;
const CATEGORY_LABELS: Record<string, string> = {
  traffic: 'Traffic',
  conversions: 'Conversions',
  financial: 'Financial',
  rates: 'Rates',
  advanced: 'Advanced',
  cap: 'Partner caps',
};

export interface MetricOption {
  value: string;
  label: string;
  /** The picker heading it sits under (a catalog with sections — the Automation's drops). */
  section?: string;
}

export function buildMetricOptions(metrics: FlowOptimizerMetricMeta[]): MetricOption[] {
  // A sectioned catalog is already in its reading order: keep it, keep the sections.
  if (metrics.some((m) => m.section)) return metrics.map((m) => ({ value: m.id, label: m.name, section: m.section }));
  // The metadata catalog is already ordered logically by category; we preserve
  // that order while exposing only the plain metric name as the label so the
  // trigger stays short ("Impressions" — not "Impressions · Traffic").
  const byCategory = new Map<string, FlowOptimizerMetricMeta[]>();
  for (const m of metrics) {
    const list = byCategory.get(m.category) ?? [];
    list.push(m);
    byCategory.set(m.category, list);
  }
  const out: MetricOption[] = [];
  for (const cat of CATEGORY_ORDER) {
    const group = byCategory.get(cat);
    if (!group?.length) continue;
    for (const m of group) {
      out.push({ value: m.id, label: m.name });
    }
  }
  return out;
}

// Exposed for tests that want to assert a fully-qualified category label
// independent of the trigger display.
export { CATEGORY_LABELS };

export function metricLabel(metrics: FlowOptimizerMetricMeta[], id: string): string {
  return metrics.find((m) => m.id === id)?.name ?? id;
}
